import { useState, useRef, useEffect } from 'react';
import ScrollReveal from 'scrollreveal';
import styles from './fitVsScreen.module.css';
import FormModal from '../../components/formModal/FormModal';
import useScrollReveal from '../../hooks/useScrollReveal';

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <path d="M20 6L9 17l-5-5" />
  </svg>
);

const XIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <path d="M18 6L6 18M6 6l12 12" />
  </svg>
);

const ArrowIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M19 12H5M12 5l-7 7 7 7" />
  </svg>
);

const yesItems = [
  { icon: <CheckIcon />, text: <span>את מרגישה שהגיע הזמן <strong>לעשות שינוי אמיתי</strong> בחיים שלך</span> },
  { icon: <CheckIcon />, text: <span>יש לך <strong>תשוקה לתחום</strong> ואת מוכנה להשקיע כדי ללמוד אותו לעומק</span> },
  { icon: <CheckIcon />, text: <span>את חולמת על <strong>עצמאות</strong> - לקבוע לעצמך מתי ואיך לעבוד</span> },
  { icon: <CheckIcon />, text: <span>את מבינה ש<strong>להשקיע בעצמך</strong> זה הצעד הכי חכם שאת יכולה לעשות</span> },
  { icon: <CheckIcon />, text: <span>את מוכנה <strong>לצאת מאזור הנוחות</strong> ולהתחיל משהו חדש</span> },
  { icon: <CheckIcon />, text: <span>את מחפשת <strong>מקצוע שנותן סיפוק</strong> - לא רק משכורת</span> },
];

const noItems = [
  { icon: <XIcon />, text: <span>את מצפה ל<strong>יומן מלא תוך חודש</strong></span> },
  { icon: <XIcon />, text: <span>את מחפשת <strong>רק כסף מהיר</strong> ואת לא באמת אוהבת את התחום</span> },
  { icon: <XIcon />, text: <span>את <strong>לא מוכנה לתרגל</strong> באופן קבוע כדי להשתפר גם אחרי הקורס</span> },
  { icon: <XIcon />, text: <span>את <strong>לא מוכנה להתמיד</strong> ולהמשיך גם כשזה לא קל</span> },
  { icon: <XIcon />, text: <span>את מחכה ש<strong>הדברים יקרו מעצמם</strong> בלי לשים את העבודה</span> },
];

function FitVsScreen() {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const sectionRef = useRef(null);
  const yesListRef = useRef(null);
  const noListRef = useRef(null);
  useScrollReveal(sectionRef);

  useEffect(() => {
    const sr = ScrollReveal();
    const baseConfig = {
      distance: '20px',
      duration: 700,
      easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
      reset: false,
      interval: 120,
    };

    if (yesListRef.current) {
      sr.reveal(yesListRef.current.children, {
        ...baseConfig,
        origin: 'right',
      });
    }
    if (noListRef.current) {
      sr.reveal(noListRef.current.children, {
        ...baseConfig,
        origin: 'left',
        delay: 200,
      });
    }
  }, []);

  return (
    <>
      <section className={styles.fit} id="fit" ref={sectionRef}>
        <span className={`${styles.star} ${styles.star1}`}>✦</span>
        <span className={`${styles.star} ${styles.star2}`}>✦</span>
        <span className={`${styles.star} ${styles.star3}`}>✧</span>
        <span className={`${styles.star} ${styles.star4}`}>✦</span>
        <span className={`${styles.star} ${styles.star5}`}>✧</span>
        <span className={`${styles.star} ${styles.star6}`}>✦</span>
        <span className={`${styles.star} ${styles.star7}`}>✧</span>
        <span className={`${styles.star} ${styles.star8}`}>✦</span>
        <div className={styles.container}>
          <div className={styles.sh} data-sr="0">
            <p className={styles.shPre}>בואי נהיה כנות לרגע</p>
            <h2 className={styles.shH2}>
              הקורסים שלי <em className={styles.em}>לא מתאימים לכולן</em> - וזה בסדר גמור
            </h2>
            <p className={styles.shSub}>רגע לפני שאנחנו מדברות - חשוב שתדעי</p>
          </div>

          <div className={styles.sectionWrapper} data-sr="0.1">
              {/* YES */}
              <div className={`${styles.section} ${styles.sectionYes}`}>
                <h2 className={`${styles.title} ${styles.titleYes}`}>את במקום הנכון אם...</h2>
                <ul className={styles.list} ref={yesListRef}>
                  {yesItems.map((item, i) => (
                    <li key={i} className={`${styles.listItem} ${styles.listItemYes}`}>
                      <div className={`${styles.iconWrapper} ${styles.iconWrapperYes}`}>
                        {item.icon}
                      </div>
                      <p className={styles.itemText}>{item.text}</p>
                    </li>
                  ))}
                </ul>
              </div>

              {/* NO */}
              <div className={`${styles.section} ${styles.sectionNo}`}>
                <h2 className={`${styles.title} ${styles.titleNo}`}>התחום פחות מתאים לך אם...</h2>
                <ul className={styles.list} ref={noListRef}>
                  {noItems.map((item, i) => (
                    <li key={i} className={`${styles.listItem} ${styles.listItemNo}`}>
                      <div className={`${styles.iconWrapper} ${styles.iconWrapperNo}`}>
                        {item.icon}
                      </div>
                      <p className={`${styles.itemText} ${styles.itemTextNo}`}>{item.text}</p>
                    </li>
                  ))}
                </ul>
              </div>
          </div>

          <div className={styles.ctaWrap} data-sr="0.2">
              <button className={styles.ctaBtn} onClick={() => setIsFormOpen(true)}>
                רומי, אני מתאימה
                <ArrowIcon />
              </button>
          </div>
        </div>
      </section>

      <FormModal isOpen={isFormOpen} onClose={() => setIsFormOpen(false)} />
    </>
  );
}

export default FitVsScreen;
